import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  BaseService, BaseStatistic,
  Game, GameUserLimitations, LeagueApi,
  PublicTeamApi, StatisticFilters,
  Team,
  TournamentApi, TournamentRound, TournamentRoundApi,
  TournamentSeasonApi, TournamentStatuses,
  TournamentTeam,
  TournamentTeamUser,
} from '@mtgame/core';
import { LeagueRootService } from '@core/services/league-root.service';

const STORE_TEAMS_KEY = 'teams';
const STORE_TEAM_KEY = 'team';
const STORE_TOURNAMENT_TEAM_KEY = 'tournament_team';
const STORE_GAMES_KEY = 'games';
const STORE_USERS_LIMITATION = 'usersLimitation';
const STORE_ROUNDS_KEY = 'rounds';
export const STORE_USERS_KEY = 'users';
export const STORE_COMMON_STATISTIC_KEY = 'commonStatistic';
export const STORE_WIN_LOSE_STATISTIC_KEY = 'winLoseStatistic';
export const STORE_BY_MONTH_STATISTIC_KEY = 'byMonthStatistic';
export const STORE_USERS_STATISTIC_KEY = 'usersStatistic';

@Injectable()
export abstract class TournamentTeamService extends BaseService {
  leagueId: number;
  teamId: number;
  filters: Partial<StatisticFilters> = {};

  get team$(): Observable<Team> {
    return this.store.get(STORE_TEAM_KEY);
  }

  get tournamentTeams$(): Observable<TournamentTeam[]> {
    return this.store.get(STORE_TEAMS_KEY);
  }

  get tournamentTeam$(): Observable<TournamentTeam> {
    return this.store.get(STORE_TOURNAMENT_TEAM_KEY);
  }

  get games$(): Observable<Game[]> {
    return this.store.get(STORE_GAMES_KEY);
  }

  get users$(): Observable<TournamentTeamUser[]> {
    return this.store.get(STORE_USERS_KEY);
  }

  get usersLimitation$(): Observable<GameUserLimitations[]> {
    return this.store.get(STORE_USERS_LIMITATION);
  }

  abstract commonStatistic$: Observable<BaseStatistic>;
  abstract winLoseStatistic$: Observable<BaseStatistic[]>;
  abstract byMonthStatistic$: Observable<BaseStatistic[]>;
  abstract usersStatistic$: Observable<BaseStatistic[]>;

  protected constructor(
    protected leagueRootService: LeagueRootService,
    protected leagueApi: LeagueApi,
    protected tournamentApi: TournamentApi,
    protected tournamentSeasonApi: TournamentSeasonApi,
    protected tournamentRoundApi: TournamentRoundApi,
    protected publicTeamApi: PublicTeamApi,
  ) {
    super();
  }

  async initialize(teamId: number, seasonId: number, tournamentId: number, round: number): Promise<any> {
    this.store.save(STORE_TEAM_KEY, null);
    this.store.save(STORE_TOURNAMENT_TEAM_KEY, null);
    this.store.save(STORE_GAMES_KEY, null);
    this.store.save(STORE_COMMON_STATISTIC_KEY, null);

    const league = this.leagueRootService.getLeagueModel();
    this.leagueId = league.id;
    this.teamId = teamId;
    const activeSeason = this.leagueRootService.getActiveSeasonModel();
    this.filters = {
      tournament_season_id: seasonId || activeSeason.id,
      tournament_id: tournamentId || null,
      tournament_round_id: round || null
    };

    await Promise.all([
      this.getTeam(),
      this.getTournamentTeams()
    ]);
    const team = this.getTournamentTeam();
    if (!team) {
      throw new Error('TeamNotFound');
    }

    if (this.filters.tournament_season_id && team.tournament.season.id !== this.filters.tournament_season_id) {
      this.filters.tournament_season_id = null;
      this.filters.tournament_id = null;
      this.filters.tournament_round_id = null;
    } else if (this.filters.tournament_id && team.tournament.id !== this.filters.tournament_id) {
      this.filters.tournament_id = null;
      this.filters.tournament_round_id = null
    }
    const tournamentTeam = this.getTournamentTeam(true);
    if (!this.filters.tournament_id) {
      this.filters.tournament_id = tournamentTeam.tournament.id;
    }
    if (!this.filters.tournament_round_id && this.filters.tournament_id &&
      this.leagueRootService.getLeagueModel().sport.isHockey()
    ) {
      const rounds = await this.getRounds(this.filters.tournament_id);
      const selectedRound = rounds[rounds.length - 1];
      if (selectedRound) {
        this.filters.tournament_round_id = selectedRound.id;
      }
    }

    await Promise.all([
      this.getUsers(),
      league.sport.isHockey() ? this.getUsersLimitation() : Promise.resolve([]),
    ]);
  }

  async getTeam(): Promise<Team> {
    const team = await this.publicTeamApi.getById(this.teamId);
    this.store.save(STORE_TEAM_KEY, team);
    return team;
  }

  async getTournamentTeams(): Promise<TournamentTeam[]> {
    const league = this.leagueRootService.getLeagueModel();
    const teams = await this.publicTeamApi.getTournamentTeams(this.teamId, league.id);
    this.store.save(STORE_TEAMS_KEY, teams);
    return teams;
  }

  getTournamentTeam(save = false): TournamentTeam {
    const teams = this.store.value(STORE_TEAMS_KEY).sort((a, b) => a.id > b.id ? -1 : 1);
    if (teams.length === 0) {
      this.store.save(STORE_TOURNAMENT_TEAM_KEY, null);
      return null;
    }
    let team = teams.find(item =>
      (!this.filters.tournament_season_id || this.filters.tournament_season_id === item.tournament.season.id) &&
      (!this.filters.tournament_id || this.filters.tournament_id === item.tournament.id) &&
      item.tournament.status === TournamentStatuses.in_progress
    );
    if (!team) {
      team = teams.find(item =>
        (!this.filters.tournament_season_id || this.filters.tournament_season_id === item.tournament.season.id) &&
        (!this.filters.tournament_id || this.filters.tournament_id === item.tournament.id)
      );
    }
    if (!team) {
      team = teams.find(item =>
        (!this.filters.tournament_season_id || this.filters.tournament_season_id === item.tournament.season.id)
      );
    }
    if (!team) {
      team = teams[0];
    }
    if (save) {
      this.store.save(STORE_TOURNAMENT_TEAM_KEY, team);
    }
    return team;
  }

  async getGames(): Promise<Game[]> {
    const team = this.store.value(STORE_TEAM_KEY);
    if (this.filters.tournament_round_id) {
      const games = await this.tournamentRoundApi.getGames(this.filters.tournament_round_id, 1, 1000, {
        teamId: team.id
      });
      this.store.save(STORE_GAMES_KEY, games.data);
      return games.data;
    }
    if (this.filters.tournament_id) {
      const games = await this.tournamentApi.getGames(this.filters.tournament_id, 1, 1000, {
        teamId: team.id,
      });
      this.store.save(STORE_GAMES_KEY, games.data);
      return games.data;
    }
    if (this.filters.tournament_season_id) {
      const games = await this.tournamentSeasonApi.getGames(this.filters.tournament_season_id, 1, 1000, {
        teamId: team.id,
      });
      this.store.save(STORE_GAMES_KEY, games.data);
      return games.data;
    }
    const games = await this.leagueApi.getGames(this.leagueId, 1, 1000, {
      teamId: team.id
    });
    this.store.save(STORE_GAMES_KEY, games.data);
    return games.data;
  }

  async getUsers(): Promise<TournamentTeamUser[]> {
    const tournamentTeam = this.store.value(STORE_TOURNAMENT_TEAM_KEY);
    const users = await this.tournamentApi.getTournamentTeamUsers(tournamentTeam.id, ['field_values']);
    this.store.save(STORE_USERS_KEY, users);
    return users;
  }

  async setFilter(filters: Partial<StatisticFilters>): Promise<void> {
    const league = this.leagueRootService.getLeagueModel();
    const seasonChanged = filters.tournament_season_id !== this.filters.tournament_season_id;
    const tournamentChanged = filters.tournament_id !== this.filters.tournament_id;
    this.filters = filters;

    if (seasonChanged || tournamentChanged) {
      this.getTournamentTeam(true);
      await Promise.all([
        this.getUsers(),
        league.sport.isHockey() ? this.getUsersLimitation() : Promise.resolve([]),
      ]);
    }

    await Promise.all([
      this.getGames(),
      this.getCommonStatistic(),
      this.getWinLoseStatistic(),
      this.getByMonthStatistic(),
      this.getUsersStatistic()
    ]);
  }

  async getUsersLimitation(): Promise<GameUserLimitations[]> {
    const tournamentTeam = this.store.value(STORE_TOURNAMENT_TEAM_KEY);
    const usersLimitation = await this.tournamentApi.getTournamentTeamUsersLimitation(tournamentTeam.id);
    this.store.save(STORE_USERS_LIMITATION, usersLimitation);
    return usersLimitation;
  }

  async getRounds(tournamentId: number): Promise<TournamentRound[]> {
    const rounds = await this.tournamentApi.getTournamentRounds(tournamentId);
    this.store.save(STORE_ROUNDS_KEY, rounds);
    return rounds;
  }

  abstract getCommonStatistic(): Promise<BaseStatistic>;
  abstract getWinLoseStatistic(): Promise<BaseStatistic[]>;
  abstract getByMonthStatistic(): Promise<BaseStatistic[]>;
  abstract getUsersStatistic(): Promise<BaseStatistic[]>;
}
